/**
 * Telemetry sink service (PROD-573).
 *
 * Replaces the PROD-578 single-line stub with a production-shape sink
 * registry. Public surface stays backwards-compatible: every existing
 * call site (`emitTelemetry(name, payload)`, `newCorrelationId()`)
 * continues to work without modification.
 *
 * Architecture (HC9 + ADR-001 Layer 4):
 *   1. Every emit attempt looks up the strict Zod schema for the event
 *      name. A hit triggers strict parse; a miss falls through to a
 *      permissive passthrough so PROD-568 / 569 / 570 / 572 events
 *      (`tenant_change`, `patient_change`, `app_init`, `tab_change`,
 *      `nba_confidence_adjusted`, `lab_basket_confirmation_required`)
 *      keep working without ceremony.
 *   2. Validated payloads are wrapped in a FHIR AuditEvent envelope
 *      (id, recorded, agent, source, entity, correlation_id) per
 *      ADR-001 Layer 4.
 *   3. The envelope is broadcast to every registered sink. The default
 *      configuration registers `consoleSink` synchronously so events
 *      are visible in dev tooling on the very first frame. The App
 *      Insights sink is added by `initializeTelemetry` once the runtime
 *      config provides a connection string.
 *   4. A strict schema failure does NOT route the original event to any
 *      sink. Instead, a `telemetry_validation_failed` meta-event is
 *      emitted carrying the original name plus a flattened error report.
 *
 * HC6 v3.3 telemetry-real: `tests/telemetry-emission.test.ts` drives
 * the actual store actions, replaces sinks with capturing fakes, and
 * asserts the structured event JSON lands in the configured sink. A
 * scaffold that compiles + lints clean but never asserts in test does
 * NOT satisfy HC9.
 *
 * HC5: zero em / en dashes anywhere.
 */
import {
  STRICT_EVENT_SCHEMAS,
  TELEMETRY_VALIDATION_FAILED,
  isStrictEventName,
  telemetryEnvelopeSchema,
  type TelemetryEnvelope,
  type TelemetryValidationFailedPayload,
} from './events';
import { consoleSink, type TelemetrySink } from './sinks/console';
import { createAppInsightsSink } from './sinks/app-insights';
import { z } from 'zod';

export type { TelemetrySink } from './sinks/console';
export type { TelemetryEnvelope } from './events';

/**
 * Backwards-compat alias kept so the legacy `TelemetryPayload` type
 * keeps working at existing call sites without a churn commit.
 */
export type TelemetryPayload = Record<string, unknown>;

export interface TelemetryEvent {
  eventName: string;
  payload: TelemetryPayload;
  ts: number;
}

/**
 * Optional emit options. Callers that already carry a correlation id
 * (e.g. the override pipeline) pass it through so the meta-events are
 * groupable.
 */
export interface EmitTelemetryOptions {
  correlation_id?: string;
  agent?: TelemetryEnvelope['agent'];
}

const DEFAULT_AGENT: TelemetryEnvelope['agent'] = {
  type: 'system',
  who: { reference: 'system/care-pathway-studio' },
};

/**
 * Resolve the build SHA via Vite's `import.meta.env.VITE_BUILD_SHA` (set
 * in CI). Falls back to 'unknown' in dev / preview where the var is not
 * exported. Pulled into a helper so the type cast lives in one place.
 */
function resolveBuildSha(): string {
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
  const raw = env?.VITE_BUILD_SHA;
  return typeof raw === 'string' && raw.length > 0 ? raw : 'unknown';
}

const DEFAULT_SOURCE: TelemetryEnvelope['source'] = {
  site: 'care-pathway-studio',
  software: {
    name: 'cps-prototype',
    version: resolveBuildSha(),
  },
};

/**
 * Sink registry. Mutable so test setup can call `setSinks([fake])` /
 * `clearSinks()` without rebuilding the module. Default registration is
 * the console sink.
 */
let registeredSinks: TelemetrySink[] = [consoleSink];

/**
 * Replace the active sink list. Used by:
 *   - `initializeTelemetry` once runtime config resolves
 *   - tests that swap in a capturing fake
 */
export function setSinks(sinks: TelemetrySink[]): void {
  registeredSinks = [...sinks];
}

/** Append one sink to the active list, returning the new length. */
export function addSink(sink: TelemetrySink): number {
  registeredSinks = [...registeredSinks, sink];
  return registeredSinks.length;
}

/** Read-only snapshot of the active sinks. */
export function getSinks(): readonly TelemetrySink[] {
  return registeredSinks;
}

/** Reset to the default (console-only) sink configuration. */
export function clearSinks(): void {
  registeredSinks = [consoleSink];
}

/**
 * Generate a correlation id for grouping related telemetry events.
 *
 * Uses `crypto.randomUUID()` when available (modern browsers + jsdom 22+)
 * and falls back to a Math.random() hex string for older runtimes. The
 * fallback is fine for the prototype; the real Application Insights
 * `operation_Id` takes over on production once the dep lands.
 */
export function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `corr-${Math.random().toString(16).slice(2, 10)}-${Date.now().toString(16)}`;
}

/**
 * Build a fresh AuditEvent envelope around the given event name +
 * payload. Pure function; no side effects.
 */
function buildEnvelope(
  eventName: string,
  entity: TelemetryPayload,
  opts: EmitTelemetryOptions,
): TelemetryEnvelope {
  // The correlation id may already be inside the payload (legacy call
  // sites pass it as a payload field). Prefer the explicit option, then
  // a payload field, then mint a new id.
  const correlationFromPayload =
    typeof entity.correlation_id === 'string' ? entity.correlation_id : null;
  const correlation_id =
    opts.correlation_id ?? correlationFromPayload ?? newCorrelationId();

  return {
    id: newCorrelationId(),
    recorded: new Date().toISOString(),
    eventName,
    agent: opts.agent ?? DEFAULT_AGENT,
    source: DEFAULT_SOURCE,
    correlation_id,
    entity,
  };
}

/**
 * Broadcast an envelope to every registered sink. Sink failures are
 * isolated (one failing sink does not block the others) but recorded as
 * `console.warn` so they surface in CI logs.
 */
function broadcast(envelope: TelemetryEnvelope): void {
  for (const sink of registeredSinks) {
    try {
      sink.emit(envelope);
    } catch (err) {
      // Recursion-safe: do NOT route the failure back through emitTelemetry.
      console.warn(`[telemetry] sink "${sink.name}" emit threw`, err);
    }
  }
}

/**
 * Emit the meta-event surfaced when a strict schema parse fails. The
 * original event is dropped; this meta-event carries enough info to
 * reconstruct the failure path during incident review.
 */
function emitValidationFailed(
  originalEventName: string,
  zodError: z.ZodError,
  correlation_id: string,
): void {
  const payload: TelemetryValidationFailedPayload = {
    original_event_name: originalEventName,
    zod_errors: zodError.issues.map((issue) => ({
      path: [...issue.path],
      message: issue.message,
      code: issue.code,
    })),
    correlation_id,
  };
  const envelope = buildEnvelope(TELEMETRY_VALIDATION_FAILED, payload, {
    correlation_id,
  });
  broadcast(envelope);
}

/**
 * Emit a telemetry event.
 *
 * Per HC9 + HC7:
 *   - If `eventName` is in `STRICT_EVENT_SCHEMAS`, the payload is parsed
 *     against the schema. A failure routes a `telemetry_validation_failed`
 *     meta-event and DOES NOT reach any sink with the original name.
 *   - If `eventName` is not in the strict registry, the payload passes
 *     through unchanged (backwards-compat with PROD-568 / 569 / 570 /
 *     572 call sites).
 *   - The validated payload is wrapped in a FHIR AuditEvent envelope and
 *     broadcast to every registered sink.
 */
export function emitTelemetry(
  eventName: string,
  payload: TelemetryPayload = {},
  opts: EmitTelemetryOptions = {},
): void {
  if (isStrictEventName(eventName)) {
    const schema = STRICT_EVENT_SCHEMAS[eventName];
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      const correlationFromPayload =
        typeof payload.correlation_id === 'string'
          ? payload.correlation_id
          : null;
      const correlation_id =
        opts.correlation_id ?? correlationFromPayload ?? newCorrelationId();
      emitValidationFailed(eventName, parsed.error, correlation_id);
      return;
    }
    const envelope = buildEnvelope(
      eventName,
      parsed.data as TelemetryPayload,
      opts,
    );
    // Sanity check the envelope itself; fail loudly in tests if the
    // wrapper drifts from the schema.
    telemetryEnvelopeSchema.parse(envelope);
    broadcast(envelope);
    return;
  }

  // Permissive path for non-strict events. The envelope schema still
  // applies so the FHIR-shape contract holds across the board.
  const envelope = buildEnvelope(eventName, payload, opts);
  broadcast(envelope);
}

/**
 * Activate the App Insights sink given a connection string. Called
 * once at app boot from `main.tsx` after the runtime config resolves.
 * No-op when `connectionString` is null or empty.
 */
export function initializeTelemetry(
  connectionString: string | null | undefined,
): void {
  if (!connectionString) return;
  const ai = createAppInsightsSink({ connectionString });
  if (ai !== null) {
    addSink(ai);
  }
}
